/**
 * @module features/profile/components/TotpSetupModal
 * @description Modal dialog for Authenticator 2FA provisioning, QR code scan, and recovery codes generation.
 */

import * as React from "react";
import {
  ShieldCheck,
  KeyRound,
  Copy,
  Check,
  Download,
  AlertCircle,
  Loader2,
  Monitor,
  Laptop,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTotpSetup, useConfirmTotp, useDisableTotp } from "../api/profile-api";
import { AppError } from "@/lib/api-client";
import { APP_NAME } from "@/config/env";

interface TotpSetupModalProps {
  open: boolean;
  isAlreadyEnabled: boolean;
  onClose: () => void;
}

export function TotpSetupModal({
  open,
  isAlreadyEnabled,
  onClose,
}: TotpSetupModalProps) {
  // Step: "init" | "verify" | "recovery" | "disable"
  const [step, setStep] = React.useState<"init" | "recovery" | "disable">(
    isAlreadyEnabled ? "disable" : "init"
  );

  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 640;
  });

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [setupData, setSetupData] = React.useState<{
    secret: string;
    qr_data_url: string;
    provisioning_uri: string;
  } | null>(null);

  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
  const [copiedSecret, setCopiedSecret] = React.useState(false);
  const [copiedCodes, setCopiedCodes] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const setupMutation = useTotpSetup();
  const confirmMutation = useConfirmTotp();
  const disableMutation = useDisableTotp();

  const prevOpenRef = React.useRef(open);

  // Load setup data when opening in "init" mode on desktop
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!wasOpen && open) {
      // Modal just opened from closed state
      if (isAlreadyEnabled) {
        setStep("disable");
        setErrorMessage(null);
        setPassword("");
      } else {
        setStep("init");
        setErrorMessage(null);
        setCode("");
        setRecoveryCodes([]);
        setCopiedCodes(false);
        setCopiedSecret(false);
        // Only request setup key if on desktop/tablet (mobile is not supported for registration)
        if (typeof window !== "undefined" && window.innerWidth >= 640) {
          setupMutation
            .mutateAsync()
            .then((data) => setSetupData(data))
            .catch(() => {
              setErrorMessage("Failed to initiate 2FA setup. Please try again.");
            });
        }
      }
    }
  }, [open, isAlreadyEnabled, setupMutation]);

  // If user resizes from mobile to desktop while modal is open in init step
  React.useEffect(() => {
    if (open && step === "init" && !isMobile && !setupData && !setupMutation.isPending) {
      setupMutation
        .mutateAsync()
        .then((data) => setSetupData(data))
        .catch(() => {
          setErrorMessage("Failed to initiate 2FA setup. Please try again.");
        });
    }
  }, [open, step, isMobile, setupData, setupMutation]);

  const handleCopySecret = () => {
    if (!setupData?.secret) return;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(setupData.secret);
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      }
    } catch {
      // Graceful fallback
    }
  };

  const handleCopyRecoveryCodes = () => {
    if (!recoveryCodes.length) return;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(recoveryCodes.join("\n"));
        setCopiedCodes(true);
        setTimeout(() => setCopiedCodes(false), 2000);
      }
    } catch {
      // Graceful fallback
    }
  };

  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes.length) return;
    try {
      const blob = new Blob(
        [
          `${APP_NAME} 2FA Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${recoveryCodes.join(
            "\n"
          )}\n\nStore these codes safely. Each code can be used once.`,
        ],
        { type: "text/plain;charset=utf-8" }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${APP_NAME.toLowerCase().replace(/[^a-z0-9]/g, "-")}-recovery-codes.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download recovery codes text file", err);
    }
  };

  const submitConfirmCode = React.useCallback(
    async (codeToConfirm: string) => {
      const trimmed = codeToConfirm.trim();
      if (!setupData?.secret || !trimmed || confirmMutation.isPending) return;

      setErrorMessage(null);
      try {
        const result = await confirmMutation.mutateAsync({
          secret: setupData.secret,
          code: trimmed,
        });
        setRecoveryCodes(result.recovery_codes);
        setStep("recovery");
      } catch (err: any) {
        if (err instanceof AppError) {
          setErrorMessage(err.errorDetails);
        } else {
          setErrorMessage("Invalid 6-digit code. Please try again.");
        }
      }
    },
    [setupData, confirmMutation]
  );

  const handleConfirmCode = (e: React.FormEvent) => {
    e.preventDefault();
    submitConfirmCode(code);
  };

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setErrorMessage(null);
    try {
      await disableMutation.mutateAsync(password);
      onClose();
    } catch (err: any) {
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
      } else {
        setErrorMessage("Failed to disable 2FA. Incorrect password.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md" sheetOnMobile>
        {step === "init" && isMobile && (
          <div>
            <DialogHeader>
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                  <Monitor className="h-4.5 w-4.5 shrink-0" />
                </div>
                <DialogTitle className="text-base font-semibold">
                  Desktop Required for 2FA Setup
                </DialogTitle>
              </div>
              <DialogDescription>
                Authenticator registration is not supported on mobile devices.
              </DialogDescription>
            </DialogHeader>

            <div className="my-4 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 dark:bg-amber-950/20 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
                  <Laptop className="h-4.5 w-4.5" />
                </div>
                <div className="space-y-1 text-xs text-muted-foreground leading-relaxed">
                  <p>
                    Setting up an authenticator requires scanning a QR code with your mobile camera or authenticator application.
                  </p>
                  <p className="font-medium text-foreground">
                    Please log in to <span className="text-foreground font-semibold">{APP_NAME}</span> from a desktop browser to register your authenticator. Mobile registration is not supported.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" onClick={onClose} className="w-full text-xs h-9 bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white">
                Got it, I'll login from desktop
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "init" && !isMobile && (
          <div>
            <DialogHeader>
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs">
                  <KeyRound className="h-4 w-4 shrink-0" />
                </div>
                <DialogTitle className="text-base font-semibold">
                  Setup {APP_NAME} Authenticator 2FA
                </DialogTitle>
              </div>
              <DialogDescription>
                Scan the QR code below using Google Authenticator, Authy, or 1Password to link your {APP_NAME} account.
              </DialogDescription>
            </DialogHeader>

            {errorMessage && (
              <div className="mt-3 flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {setupMutation.isPending || !setupData ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-foreground" />
                <p className="text-xs text-muted-foreground font-mono">
                  Generating secret key...
                </p>
              </div>
            ) : (
              <form onSubmit={handleConfirmCode} className="space-y-4 pt-3">
                {/* QR Code Canvas */}
                <div className="flex flex-col items-center justify-center p-3 bg-white rounded-xl border border-border">
                  <img
                    src={setupData.qr_data_url}
                    alt="Authenticator QR Code"
                    className="h-44 w-44 object-contain"
                  />
                </div>

                {/* Manual Secret Key */}
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                    Manual Entry Key
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={setupData.secret}
                      className="font-mono text-xs h-9 bg-muted/40"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCopySecret}
                      className="h-9 w-9 shrink-0"
                      title="Copy Secret"
                    >
                      {copiedSecret ? (
                        <Check className="h-4 w-4 text-success" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="totp_confirm_code"
                    className="text-xs font-medium text-foreground"
                  >
                    Enter 6-Digit Code from Authenticator
                  </label>
                  <Input
                    id="totp_confirm_code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    placeholder="000000"
                    maxLength={6}
                    value={code}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/\D/g, "");
                      setCode(clean);
                      if (clean.length === 6 && !confirmMutation.isPending) {
                        submitConfirmCode(clean);
                      }
                    }}
                    className="font-mono text-center text-lg tracking-[0.25em] h-11"
                  />
                  <p className="text-[11px] text-muted-foreground text-center">
                    Auto-submits instantly when 6 digits are entered
                  </p>
                </div>

                <DialogFooter className="pt-2">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={code.length < 6 || confirmMutation.isPending}
                    className="min-w-[100px] bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
                  >
                    {confirmMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin text-white dark:text-zinc-950" />
                    ) : (
                      "Verify & Enable"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </div>
        )}

        {step === "recovery" && (
          <div>
            <DialogHeader>
              <div className="flex items-center gap-2 text-success">
                <ShieldCheck className="h-5 w-5" />
                <DialogTitle>2FA Enabled Successfully!</DialogTitle>
              </div>
              <DialogDescription>
                Save these 8 single-use recovery codes. You can use them to sign in if you lose access to your authenticator.
              </DialogDescription>
            </DialogHeader>

            <div className="my-4 p-4 rounded-xl border border-border bg-muted/40 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((rc, idx) => (
                  <div
                    key={idx}
                    className="p-2 rounded bg-card text-foreground font-mono text-xs text-center border border-border tracking-wider font-semibold"
                  >
                    {rc}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyRecoveryCodes}
                  className="text-xs"
                >
                  {copiedCodes ? (
                    <Check className="mr-1.5 h-3.5 w-3.5 text-success" />
                  ) : (
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  {copiedCodes ? "Copied!" : "Copy Codes"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadRecoveryCodes}
                  className="text-xs"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Download .txt
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={onClose} className="w-full bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white">
                I have saved my recovery codes
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "disable" && (
          <div>
            <DialogHeader>
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
              </div>
              <DialogDescription>
                Disabling 2FA reduces account security. Enter your current password to proceed.
              </DialogDescription>
            </DialogHeader>

            {errorMessage && (
              <div className="mt-3 flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleDisable2FA} className="space-y-4 pt-3">
              <div className="space-y-1">
                <label
                  htmlFor="disable_password"
                  className="text-xs font-medium text-foreground"
                >
                  Current Password
                </label>
                <Input
                  id="disable_password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-10 text-sm font-mono"
                />
              </div>

              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={!password || disableMutation.isPending}
                >
                  {disableMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Disable 2FA"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
